import { useReducer, useEffect, useCallback } from 'react';

import { useLocation, useParams } from 'react-router';

import CustomCampaign from 'domains/campaigns/components/CustomCampaign/CustomCampaign';
import { setDraftState } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import {
    customCurationReducer,
    directMailInitialState,
    emailInitialState,
    smsInitialState,
    voiceCallInitialState,
    CampaignStateTypes,
} from 'domains/campaigns/components/CustomCampaign/customCampaignReducer';
import { CampaignChannels, MIXED_CHANNEL } from 'domains/campaigns/types';
import { transformDraftSendFromApi } from 'domains/campaigns/utils';
import useDraftCampaign from 'hooks/queries/useDraftCampaign';

const CustomPage = () => {
    const location: { state?: { channel?: CampaignChannels | typeof MIXED_CHANNEL } } = useLocation();
    const { campaignId } = useParams<{ campaignId?: string }>();

    const channel = location?.state?.channel ?? CampaignChannels.EMAIL;

    const { data: campaignData, isSuccess } = useDraftCampaign(campaignId || '', {
        enabled: !!campaignId,
    });

    const getInitialState = useCallback((): CampaignStateTypes => {
        // A mixed-channel campaign's first send starts unconfigured (same placeholder shape as
        // Email) but isMixedChannel routes it to the merged content picker instead of straight
        // into the Email content step, so no channel is actually assumed.
        if (channel === MIXED_CHANNEL) return { ...emailInitialState, isMixedChannel: true };
        if (channel === CampaignChannels.DIRECT_MAIL) return directMailInitialState;
        if (channel === CampaignChannels.EMAIL) return emailInitialState;
        if (channel === CampaignChannels.SMS) return smsInitialState;
        if (channel === CampaignChannels.VOICE_CALL) return voiceCallInitialState;
    }, [channel]);

    const initialState = getInitialState();
    const [state, dispatch] = useReducer(customCurationReducer, initialState);

    useEffect(() => {
        if (isSuccess && campaignData && campaignId) {
            const transformedSends = (campaignData.sends || []).map((apiSend: any, _index: number, allSends: any[]) =>
                transformDraftSendFromApi(apiSend, allSends)
            );

            const hasSends = transformedSends.length > 0;
            const initialSends = getInitialState().sends;

            const campaignState: CampaignStateTypes = {
                name: campaignData.name || '',
                type: campaignData.type,
                sends: hasSends ? transformedSends : initialSends,
                ...(campaignData?.reentryDetails && { reentryDetails: campaignData?.reentryDetails }),
                ...(campaignData?.abTestConfig && { abTestConfig: campaignData?.abTestConfig }),
                exitCriteria: campaignData?.exitCriteria ?? null,
                // Backend derives 'MIXED' once a campaign's sends actually span multiple channels.
                // A resumed draft that started as Mixed but has only one send so far won't be
                // detected here yet — it re-enters the merged picker once a second, different-
                // channel send is added.
                // Fallback: derive from sends directly for older drafts where the backend field
                // may not be set yet — if any two sends differ in channel it's a mixed campaign.
                isMixedChannel:
                    (campaignData as any)?.channel === MIXED_CHANNEL ||
                    !!(campaignData as any)?.isMixedChannel ||
                    !!(campaignData as any)?.is_mixed_channel ||
                    (hasSends && new Set(transformedSends.map((s: any) => s.channel).filter(Boolean)).size > 1),
            };

            dispatch(setDraftState(campaignState));
        }
    }, [isSuccess, campaignData, campaignId, getInitialState]);

    return <CustomCampaign campaignState={state} dispatchCampaignAction={dispatch} campaignId={campaignId} />;
};

export default CustomPage;
